import React, { useEffect, useState, useRef } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { Button, Modal } from "react-bootstrap";
import { useDispatch } from "react-redux";
import "../../userLayout/userLayout.scss";
import { toast } from "sonner";
import { logoutUser, resetAuthState } from "../../../redux/slices/authSlice";
import { isAuthenticated } from "../../../utils/auth";
import { useTheme } from "../../../context/ThemeContext";
import logoDark from "../../../assets/images/logo-dark.svg";

import { ReactComponent as ToggleLogo } from "../../../assets/images/toggle-logo.svg";

//Sidebar Icons
import { ReactComponent as Home } from "../../../assets/images/dashboard-icons/home.svg";
import { ReactComponent as Search } from "../../../assets/images/dashboard-icons/search.svg";
import { ReactComponent as Jobs } from "../../../assets/images/dashboard-icons/jobs.svg";
import { ReactComponent as RelocationPlanner } from "../../../assets/images/dashboard-icons/relocation-planner.svg";
import { ReactComponent as Comparison } from "../../../assets/images/dashboard-icons/comparison.svg";
import { ReactComponent as Invest } from "../../../assets/images/dashboard-icons/invest.svg";
import { ReactComponent as Calculators } from "../../../assets/images/dashboard-icons/calculators.svg";
import { ReactComponent as Community } from "../../../assets/images/dashboard-icons/community.svg";
import { ReactComponent as Support } from "../../../assets/images/dashboard-icons/support.svg";
import { ReactComponent as Account } from "../../../assets/images/dashboard-icons/account.svg";
import { ReactComponent as Logout } from "../../../assets/images/dashboard-icons/logout.svg";
import { ReactComponent as Discounts } from "../../../assets/images/dashboard-icons/discounts.svg";
import { ReactComponent as LogoutModalIcon } from "../../../assets/images/logout-modal.svg";
import { ReactComponent as ToggleAppIcon } from "../../../assets/images/toggle-app-icon.svg";
import { ReactComponent as ToggleAndroindIcon } from "../../../assets/images/toogle-android-icon.svg";

import { ReactComponent as WithToggleAppIcon } from "../../../assets/images/with-toggle-app-icon.svg";
import { ReactComponent as WithToggleAndroindIcon } from "../../../assets/images/with-toogle-android-icon.svg";
import { ReactComponent as AndroidIconDark } from "../../../assets/images/android-icon-dark.svg";
import { ReactComponent as AppleIconDark } from "../../../assets/images/apple-icon-dark.svg";
import { ReactComponent as MenuDown } from "../../../assets/images/menu-down.svg";
import { ReactComponent as MenuUp } from "../../../assets/images/menu-up.svg";
import { ReactComponent as Tour } from "../../../assets/images/dashboard-icons/tour.svg";
import { ReactComponent as Shop } from "../../../assets/images/dashboard-icons/shop.svg";
import { searchSessionEnd } from "../../../redux/slices/searchSlice";
import { ReactComponent as Blog } from "../../../assets/images/dashboard-icons/blog.svg";
import { ReactComponent as Messages } from "../../../assets/images/dashboard-icons/messages.svg";
import { ReactComponent as AppGuideIcon } from "../../../assets/images/dashboard-icons/book.svg";

const items = [
  { to: "/dashboard", label: "Home", icon: <Home /> },
  {
    to: "/salaries-paychecks",
    label: "Salaries & Paychecks",
    icon: <Invest />,
  },
  {
    to: "/mortgage",
    label: "Mortgage Calculator",
    icon: <Calculators />,
  },
  { to: "/comparison", label: "Comparison", icon: <Comparison /> },
  { to: "/jobs", label: "Jobs", icon: <Jobs /> },
  { to: "/search", label: "Search Cities", icon: <Search /> },
  { to: "/community", label: "Community", icon: <Community /> },
  { to: "/messaging", label: "Messages", icon: <Messages /> },
  // {
  //   external: "https://mapmypay.com/blog",
  //   label: "Blogs",
  //   icon: <Blog />,
  // },
  {
    to: "/blog",
    label: "Blogs",
    icon: <Blog />,
  },
  {
    to: "/app-guide",
    label: "App Guide",
    icon: <AppGuideIcon />,
  },
  // {
  //   to: "/tour",
  //   label: " Take a Tour",
  //   icon: <Tour />,
  // },
  // {
  //   label: "Shop",
  //   icon: <Shop />,
  //   children: [
  //     { to: "/shop/products", label: "Products" },
  //     {
  //       to: "/shop/cart",
  //       label: "Cart",
  //     },
  //     {
  //       to: "/shop/my-orders",
  //       label: "My Orders",
  //     }
  //   ],
  // },
  // {
  //   label: "Calculators",
  //   icon: <Calculators />,
  //   children: [
  //     { to: "/calculators/retirement", label: "Retirement/Invest" },
  //     {
  //       to: "/calculators/life-insurance",
  //       label: "Life Insurance",
  //     },
  //     {
  //       to: "/calculators/home-insurance",
  //       label: "Home Insurance",
  //     },
  //     { to: "/calculators/studentloan", label: "Student Loan" },
  //   ],
  // },
  // {
  //   to: "/discounts",
  //   label: "Deals",
  //   icon: <Discounts />,
  // },
];

const PublicSidebar = ({
  mobileMenuOpen,
  closeMobileMenu,
  isPublicPage = false,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { resolvedTheme } = useTheme();
  const sidebarRef = useRef(null);

  const [collapsed, setCollapsed] = useState(true);
  const [isHovered, setIsHovered] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [isLogoutActive, setIsLogoutActive] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [expandedItem, setExpandedItem] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const prevPathRef = useRef(location.pathname);
  const blockedRoutes = [];
  const isAuth = isAuthenticated();

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 992); // Bootstrap lg breakpoint
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    try {
      const stored = localStorage.getItem("dashCollapsed");
      if (stored !== null) {
        const parsed = stored === "true";
        setCollapsed(parsed);
        document.body.classList.toggle("dash-collapsed", parsed);
        return;
      }
    } catch (e) {}

    const hasCollapsedClass = document.body.classList.contains(
      "dash-collapsed",
    );
    setCollapsed(hasCollapsedClass);
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem("dashCollapsed", String(collapsed));
    } catch (e) {}

    if (!isMobile) {
      document.body.classList.toggle("dash-collapsed", collapsed && !isHovered);
      document.body.classList.toggle(
        "dash-hover-expanded",
        collapsed && isHovered,
      );
    } else {
      document.body.classList.toggle("dash-collapsed", collapsed);
      document.body.classList.remove("dash-hover-expanded");
    }
  }, [collapsed, isHovered, isMobile]);

  useEffect(() => {
    const prevPath = prevPathRef.current;
    const currentPath = location.pathname;

    if (prevPath === "/search" && currentPath !== "/search") {
      const session_id = sessionStorage.getItem("session_id");

      if (session_id) {
        const payload = { session_id };

        if (currentPath.includes("/dashboard")) {
          payload.led_to_comparison = true;
        }

        if (currentPath.includes("/jobs")) {
          payload.led_to_job_view = true;
        }

        dispatch(searchSessionEnd(payload));
      }
    }

    prevPathRef.current = currentPath;

    if (closeMobileMenu) closeMobileMenu();
    setIsHovered(false);
  }, [location.pathname]);

  const handleMouseEnter = () => {
    if (!isMobile && collapsed && !blockedRoutes.includes(location.pathname)) {
      setIsHovered(true);
    }
  };

  const handleMouseLeave = () => {
    if (!isMobile) {
      setIsHovered(false);
    }
  };

  const handleLogoutClick = (e) => {
    e.preventDefault();
    setIsLogoutActive(true);
    setShowModal(true);
  };

  const toggle = () => {
    if (blockedRoutes.includes(location.pathname)) {
      return;
    }

    setCollapsed((prev) => !prev);

    setIsHovered(false);
  };

  const toggleSubmenu = (label) => {
    setExpandedItem((prev) => (prev === label ? null : label));
  };

  const handleNavClick = (e, to) => {
    // If user is not authenticated and on public page, redirect to registration with intended destination
    if (!isAuth && isPublicPage) {
      e.preventDefault();
      // Save the intended destination
      navigate("/registration", {
        state: {
          redirectTo: to,
          fromPublicJob: true,
        },
      });
      return;
    }

    if (closeMobileMenu) {
      closeMobileMenu();
    }
  };

  const handleLogoutConfirm = async () => {
    if (isLoggingOut) return;
    setIsLoggingOut(true);
    await dispatch(logoutUser());
    dispatch(resetAuthState());
    setShowModal(false);
    setIsLogoutActive(false);
    toast.success("Logged out successfully!");
    navigate("/login", { replace: true });
  };

  const handleLogoutCancel = () => {
    setShowModal(false);
    setIsLogoutActive(false);
  };

  const isToggleDisabled = blockedRoutes.includes(location.pathname);

  return (
    <>
      <div
        ref={sidebarRef}
        className={`sidebar ${
          mobileMenuOpen ? "d-block mobile-menu-overlay" : ""
        } ${collapsed && isHovered ? "hover-expanded" : ""}`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div
          className={`sidebar-content ${
            mobileMenuOpen ? "mobile-menu-open" : ""
          }`}
        >
          {/* Mobile close button */}
          <button
            className="mobile-close-btn d-lg-none"
            onClick={closeMobileMenu}
            aria-label="Close mobile menu"
          >
            <span className="close-line"></span>
            <span className="close-line"></span>
          </button>

          <div className="logo">
            <NavLink to="/">
              <img
                src={
                  resolvedTheme === "dark"
                    ? logoDark
                    : process.env.PUBLIC_URL + "/logo.svg"
                }
                alt="MAP my pay"
              />
            </NavLink>
          </div>
          <div className="logo on-collapsed">
            <NavLink to="/">
              <ToggleLogo />
            </NavLink>
          </div>

          <ul className="sidebar-menu">
            {items.map(({ to, label, icon, children }) => (
              <li key={label} className={children ? "has-submenu" : ""}>
                {children ? (
                  <>
                    <a
                      role="button"
                      className={`nav-link ${expandedItem === label ? "" : ""}`}
                      onClick={() => toggleSubmenu(label)}
                      aria-expanded={expandedItem === label}
                    >
                      <span className="icon">{icon}</span>
                      <span className="label">{label}</span>
                      <span className="arrow" aria-hidden="true">
                        {expandedItem === label ? <MenuUp /> : <MenuDown />}
                      </span>
                    </a>

                    {expandedItem === label && (
                      <ul className="submenu">
                        {children.map((child) => (
                          <li key={child.to}>
                            <NavLink
                              to={child.to}
                              className={({ isActive }) =>
                                `nav-link ${isActive ? "active" : ""}`
                              }
                              onClick={(e) => handleNavClick(e, child.to)}
                            >
                              <span className="label">{child.label}</span>
                            </NavLink>
                          </li>
                        ))}
                      </ul>
                    )}
                  </>
                ) : (
                  <NavLink
                    end={to === "/dashboard"}
                    to={to}
                    className={({ isActive }) =>
                      `nav-link ${isActive ? "active" : ""}`
                    }
                    onClick={(e) => handleNavClick(e, to)}
                  >
                    <span className="icon" aria-hidden>
                      {icon}
                    </span>
                    <span className="label">{label}</span>
                  </NavLink>
                )}
              </li>
            ))}

            {/* Logout button - only show if authenticated */}
            {isAuth && (
              <li>
                <button
                  type="button"
                  className={`nav-link ${isLogoutActive ? "active" : ""}`}
                  onClick={handleLogoutClick}
                >
                  <span className="icon" aria-hidden>
                    <Logout />
                  </span>
                  <span className="label">Logout</span>
                </button>
              </li>
            )}
          </ul>

          <div className="app-icons">
            <a
              href="https://apps.apple.com/us/app/map-my-pay/id6747190135"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Download on App Store"
              className="toggle-icon"
            >
              {resolvedTheme === "dark" ? (
                <AppleIconDark className="theme-badge" />
              ) : (
                <ToggleAppIcon />
              )}
            </a>
            <a
              href="https://play.google.com/store/apps/details?id=com.mapmypay"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Get it on Google Play"
              className="toggle-icon"
            >
              {resolvedTheme === "dark" ? (
                <AndroidIconDark className="theme-badge" />
              ) : (
                <ToggleAndroindIcon />
              )}
            </a>
          </div>
          <div className="app-icons with-toogle">
            <a
              href="https://apps.apple.com/us/app/map-my-pay/id6747190135"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Download on App Store"
              className="toggle-icon"
            >
              <WithToggleAppIcon />
            </a>
            <a
              href="https://play.google.com/store/apps/details?id=com.mapmypay"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Get it on Google Play"
              className="toggle-icon"
            >
              <WithToggleAndroindIcon />
            </a>
          </div>

          {/* Sidebar Toggle Button */}
          <button
            type="button"
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            className={`sidebar-toggle${collapsed ? " is-collapsed" : ""}${
              isToggleDisabled ? " " : ""
            }`}
            onClick={toggle}
            aria-expanded={!collapsed}
            disabled={isToggleDisabled}
            title={
              !isToggleDisabled
                ? "Toggle disabled on this page"
                : "Toggle sidebar"
            }
          />
          <p className="copy-right mobile">v3.1.1 © 2026 MapMyPay LLC</p>
        </div>
        <p className="copy-right desktop">v3.1.1 © 2026 MapMyPay LLC</p>
      </div>

      {/* Logout Modal */}
      {showModal && (
        <Modal
          show={showModal}
          onHide={handleLogoutCancel}
          centered
          dialogClassName="custom-modal logout-modal"
        >
          <Modal.Body>
            <div className="icon">
              <LogoutModalIcon />
            </div>
            <h3 className="title">Logout</h3>
            <p>Are you sure you want to logout?</p>
            <div className="actions">
              <Button
                className="btn btn-secondary"
                onClick={handleLogoutCancel}
              >
                No
              </Button>
              <Button
                className="btn btn-primary"
                onClick={handleLogoutConfirm}
                disabled={isLoggingOut}
              >
                {isLoggingOut ? "Logging out..." : "Yes"}
              </Button>
            </div>
          </Modal.Body>
        </Modal>
      )}
    </>
  );
};

export default PublicSidebar;
